import { ISoundService, SoundType, IEventResultStore } from 'eTip--common';

export interface IScoreChangeMonitor {
    /**
     * Any event has changed, need to check wheather it's favorite (sound of goal should be played).
     * Main result may be null for e.g. Formula 1.
     * */
    eventUpdated(eventId: number, categoryId: number, mainScore: IEventResultStore | null): void;

    /**
     * Favorite events (sound of goal to be played) have changed, contains full list of available events to check (for maintenance).
     * Main result may be null for e.g. Formula 1.
     * @param idWithScores
     */
    favoriteEventsUpdated(idWithScores: Map<number, IEventResultStore | null>): void;

    /**
     * Sets provider to decide wheather event is in favorites (sound of goal should be played)
     * @param eventIsFavoriteProvider_
     */
    setEventIsFavoriteProvider(eventIsFavoriteProvider_: IEventIsFavoriteProvider): void;
}

export interface IEventIsFavoriteProvider {
    isEventFavorite(eventId: number, categoryId: number): boolean;
}

/**
 * Monitors score change in (favorite) events to play sound of goal. At start it needs to assign eventIsFavoriteProvider using setEventIsFavoriteProvider.
 * @param soundService
 */
export function createScoreChangeMonitor(soundService: ISoundService): IScoreChangeMonitor {

    const numberOfCallsToMaintenance = 50;

    let eventsWithPlayedMainScoreHash: Map<number, string> = new Map<number, string>();
    let eventIsFavoriteProvider: IEventIsFavoriteProvider;
    let numberOfCallsFromMaintenance = 0;

    const eventUpdated = (eventId: number, categoryId: number, mainScore: IEventResultStore | null): void => {
        if (!eventIsFavoriteProvider.isEventFavorite(eventId, categoryId)) {
            return;
        }

        if (checkEvent(eventId, mainScore)) {
            doPlaySound();
        }
    }

    /**
     * Returns whether there is a change in score.
     * @param eventId
     * @param mainScore
     */
    const checkEvent = (eventId: number, mainScore: IEventResultStore | null): boolean => {

        if (!mainScore) {
            return false;
        }

        let scoreHash = getHashOfScore(mainScore);

        if (eventsWithPlayedMainScoreHash.has(eventId)) {
            if (eventsWithPlayedMainScoreHash.get(eventId) !== scoreHash) {
                eventsWithPlayedMainScoreHash.set(eventId, scoreHash);
                return true;
            }
        } else {
            eventsWithPlayedMainScoreHash.set(eventId, scoreHash);
        }
        return false;
    };

    const favoriteEventsUpdated = (idWithScores: Map<number, IEventResultStore | null>): void => {

        let wasChange = false;
        let idsToRemove = new Set<number>();
        idWithScores.forEach((mainScore: IEventResultStore, eventId: number) => {
            wasChange = wasChange || checkEvent(eventId, mainScore);
        });

        if (wasChange) {
            doPlaySound();
        }

        numberOfCallsFromMaintenance++;
        if (numberOfCallsFromMaintenance > numberOfCallsToMaintenance) {
            maintainListOfEvents(idWithScores, idsToRemove);
            numberOfCallsFromMaintenance = 0;
        }

    }

    const maintainListOfEvents = (idWithScores: Map<number, IEventResultStore>, idsToRemove: Set<number>) => {
        eventsWithPlayedMainScoreHash.forEach((_scoreHash: string, eventId: number) => {
            if (!idWithScores.has(eventId)) {
                idsToRemove.add(eventId);
            }
        });
        idsToRemove.forEach(id => eventsWithPlayedMainScoreHash.delete(id));
    }

    const doPlaySound = (): void => {
        soundService.playSound(SoundType.Goal);
    }

    const getHashOfScore = (score: IEventResultStore): string => {
        return score.score1 + ":" + score.score2;
    }

    const assignEventListStore = (eventIsFavoriteProvider_: IEventIsFavoriteProvider): void => {
        eventIsFavoriteProvider = eventIsFavoriteProvider_;
    }

    return {
        eventUpdated,
        favoriteEventsUpdated,
        setEventIsFavoriteProvider: assignEventListStore
    }
}

export function createEmptyScoreChangeMonitor(): IScoreChangeMonitor {

    const eventUpdated = (_eventId: number, _categoryId: number, _mainScore: IEventResultStore | null): void => {
    }

    const favoriteEventsUpdated = (_idWithScores: Map<number, IEventResultStore | null>): void => {
    }

    const assignEventListStore = (_eventIsFavoriteProvider: IEventIsFavoriteProvider): void => {
    }

    return {
        eventUpdated,
        favoriteEventsUpdated,
        setEventIsFavoriteProvider: assignEventListStore
    }
}